import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight, Check } from "lucide-react";
import { Reveal } from "../motion/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOLUTIONS = [
  "Computer Peripherals",
  "Enterprise Networking",
  "CCTV Surveillance",
  "UPS & Power Backup",
  "Printers & Scanners",
  "Multiple / Not sure",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    solution: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in your name, email, phone and message.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quotes`, form);
      toast.success(res.data?.message || "Thank you — we’ll reach out shortly.");
      setSuccess(true);
      setForm({ name: "", company: "", email: "", phone: "", solution: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-[#FAFAFA] py-24 lg:py-32" data-testid="contact-section">
      {/* CTA banner */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-16 lg:mb-24">
        <Reveal>
          <div className="rounded-xl bg-[#222222] text-[#FAFAFA] p-10 lg:p-16 relative overflow-hidden border border-[#9A9A9A]/20">
            <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#A4161A]/15 blur-3xl" aria-hidden />
            <div className="section-label mb-6">A Trusted Partner</div>
            <h3 className="font-display text-[32px] sm:text-[44px] lg:text-[60px] leading-[1.02] tracking-[-0.03em] max-w-[22ch]">
              Looking for a trusted Enterprise IT partner? <span className="text-[#A4161A]">Let's discuss your requirements.</span>
            </h3>
          </div>
        </Reveal>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left: contact info */}
        <div className="lg:col-span-5">
          <Reveal>
            <div className="section-label !text-[#9A9A9A] mb-6">Contact Sales</div>
            <h2 className="font-display text-[#222222] text-[36px] sm:text-[48px] lg:text-[64px] leading-[0.98] tracking-[-0.03em] mb-10">
              Let's build<br />what your business<br /><span className="text-[#A4161A]">actually needs</span>.
            </h2>
            <p className="text-[#444444] font-body text-[15px] leading-relaxed max-w-[42ch] mb-12">
              Share your requirement — infrastructure sizing, procurement, or a tender you're
              chasing. A senior engineer will respond within one business day.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <ul className="space-y-8">
              <li className="flex items-start gap-4" data-testid="contact-email">
                <div className="mt-1 w-10 h-10 rounded-lg bg-[#222222] text-[#A4161A] flex items-center justify-center flex-shrink-0">
                  <Mail size={16} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#9A9A9A] font-btn mb-1">Email</div>
                  <a href="mailto:info@dbcomputronics.in" className="text-[#222222] font-display text-[18px] lg:text-[22px] red-underline">
                    info@dbcomputronics.in
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-phone">
                <div className="mt-1 w-10 h-10 rounded-lg bg-[#222222] text-[#A4161A] flex items-center justify-center flex-shrink-0">
                  <Phone size={16} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#9A9A9A] font-btn mb-1">Phone</div>
                  <div className="flex flex-col gap-1">
                    <a href="tel:+919963645702" className="text-[#222222] font-display text-[18px] lg:text-[22px] red-underline">+91 99636 45702</a>
                    <a href="tel:+919290305011" className="text-[#222222] font-display text-[18px] lg:text-[22px] red-underline">+91 92903 05011</a>
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-address">
                <div className="mt-1 w-10 h-10 rounded-lg bg-[#222222] text-[#A4161A] flex items-center justify-center flex-shrink-0">
                  <MapPin size={16} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#9A9A9A] font-btn mb-1">Office</div>
                  <div className="text-[#222222] font-body text-[15px] leading-relaxed max-w-[36ch]">
                    Chenoy Trade Centre, Park Lane,<br />
                    Secunderabad, Hyderabad — 500003,<br />
                    Telangana, India
                  </div>
                </div>
              </li>
            </ul>
          </Reveal>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              className="bg-white border border-[#9A9A9A]/20 rounded-xl p-8 lg:p-12"
              data-testid="quote-form"
              noValidate
            >
              <div className="flex items-center justify-between mb-8">
                <div className="section-label !text-[#9A9A9A]">Request a Quote</div>
                <div className="text-[10px] font-btn tracking-[0.28em] uppercase text-[#9A9A9A]">Reply in 1 business day</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-[#9A9A9A] font-btn">Full Name *</label>
                  <input value={form.name} onChange={upd("name")} required data-testid="input-name"
                    className="input-field" placeholder="Your name" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-[#9A9A9A] font-btn">Company</label>
                  <input value={form.company} onChange={upd("company")} data-testid="input-company"
                    className="input-field" placeholder="Company / organisation" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-[#9A9A9A] font-btn">Email *</label>
                  <input type="email" value={form.email} onChange={upd("email")} required data-testid="input-email"
                    className="input-field" placeholder="you@company.com" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-[#9A9A9A] font-btn">Phone *</label>
                  <input type="tel" value={form.phone} onChange={upd("phone")} required data-testid="input-phone"
                    className="input-field" placeholder="+91" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] tracking-[0.25em] uppercase text-[#9A9A9A] font-btn">Solution of interest</label>
                  <select
                    value={form.solution}
                    onChange={upd("solution")}
                    data-testid="input-solution"
                    className="input-field bg-transparent"
                  >
                    <option value="">— Select —</option>
                    {SOLUTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] tracking-[0.25em] uppercase text-[#9A9A9A] font-btn">How can we help? *</label>
                  <textarea
                    value={form.message}
                    onChange={upd("message")}
                    required
                    rows={4}
                    data-testid="input-message"
                    className="input-field resize-none"
                    placeholder="Briefly describe your requirement, site count, and timeline…"
                  />
                </div>
              </div>

              <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
                <div className="text-[12px] text-[#9A9A9A] font-body max-w-[36ch]">
                  By submitting, you agree to be contacted by our enterprise sales team.
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-red inline-flex items-center gap-3 px-7 py-4 rounded-lg font-btn text-[12px] tracking-[0.22em] uppercase disabled:opacity-70"
                  data-testid="submit-quote"
                >
                  <span>{submitting ? "Sending…" : success ? "Sent ✓" : "Send Request"}</span>
                  {success ? <Check size={16} className="relative z-10" /> : <ArrowRight size={16} className="relative z-10" />}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
